/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState, useEffect, useRef } from 'react';
import { Card, Direction, Margin, Column, SubHeading, CardAccordion } from '@scotia/canvas-react';
import { dlParentPush } from '@cldf/scotiaweb-gtm';
import { getInStorage, saveInStorage } from '@cldf/scotiaweb-web-storage';
import { getAllInSession } from '@cldf/scotiaweb-customer-information';
import RealTimeOffer from 'rto-spl-web-cl';
import styles from './ProductSummary.scss';
import AccountContent from './components/Account/AccountContent';
import { getProducts } from '../../../services';
import CardContent from './components/Card/CardContent';
import CreditContent from './components/Credit/CreditContent';
import InvestmentContent from './components/Investment/InvestmentContent';
import ProductsHeader from './components/ProductsHeader/ProductsHeader';
import ContentContainer from './components/ContentContainer';
import { SUMMARY_PRODUCTS_INITIAL_STATE } from './summary.products.initial.state';
import Kyc from '../../kyc';

export const doElementContainsSomething = (element, exclude = undefined) => {
  if (element) {
    let exist = false;
    if (Array.isArray(element)) {
      exist = typeof element !== 'undefined' && element.length > 0;
    } else if (typeof element === 'object') {
      const obj = element;
      Object.keys(obj).forEach(key => {
        if (obj[key] && obj[key].length > 0 && (!exclude || !exclude.length == 0 || exclude.indexOf(key) === -1)) {
          exist = true;
        }
      });
    }
    return exist;
  }
  return false;
};

/**
 *
 * @param {Integer} chevronPosition
 * Get the chevron in the position to simulate a click.
 */
/* istanbul ignore next */
const simulateProductIconChevronClick = chevronPosition => {
  const respectiveSelectedProductIconCHevron = document.getElementsByClassName('button accordion__toggle')[
    chevronPosition
  ];
  if (respectiveSelectedProductIconCHevron) {
    respectiveSelectedProductIconCHevron.click();
  }
};

export const cardAccordionItems = ({
  listProducts,
  showSald,
  onHideSaldHandler,
  error,
  switchSummary,
  summariesInfo,
  currentSummary,
  recallService,
  categorySectionRefs,
  showBannerRTO,
}) => [
  {
    title: (
      <ProductsHeader
        reference={categorySectionRefs.ACCOUNTS}
        onClick={() => {
          /* istanbul ignore next */
          simulateProductIconChevronClick(0);
        }}
        error={error}
        showInputToggle={
          currentSummary === summariesInfo.ACCOUNTS.id &&
          !summariesInfo.ACCOUNTS.closed &&
          summariesInfo.ACCOUNTS.loaded &&
          doElementContainsSomething(listProducts.accounts, ['credit_cards'])
        }
        showSald={showSald}
        onHideSaldHandler={onHideSaldHandler}
        product="Cuentas"
      />
    ),
    content: (
      <ContentContainer
        id={summariesInfo.ACCOUNTS.id}
        loading={!summariesInfo.ACCOUNTS.loaded && !error}
        error={error}
        action={() => {
          /* istanbul ignore next */
          recallService();
        }}
        exclude={['credit_cards']}
        newProductMessage={summariesInfo.ACCOUNTS.newProductMessage}
        newProductAction={summariesInfo.ACCOUNTS.newProductAction}
        banner={summariesInfo.ACCOUNTS.banner}
        mercury={summariesInfo.ACCOUNTS.mercury}
      >
        <AccountContent list={listProducts.accounts} showSald={showSald} />
      </ContentContainer>
    ),
    onClick: () => {
      /* istanbul ignore next */
      switchSummary(summariesInfo.ACCOUNTS.id);
    },
  },
  {
    title: (
      <ProductsHeader
        onClick={() => {
          /* istanbul ignore next */
          simulateProductIconChevronClick(1);
        }}
        reference={categorySectionRefs.CARDS}
        product="Tarjetas"
      />
    ),
    content: (
      <ContentContainer
        id={summariesInfo.CARDS.id}
        loading={!summariesInfo.CARDS.loaded}
        error={error}
        action={() => {
          /* istanbul ignore next */
          recallService();
        }}
        exclude={['debit_accounts', 'checking_accounts', 'saving_accounts']}
        newProductMessage={summariesInfo.CARDS.newProductMessage}
        newProductAction={summariesInfo.CARDS.newProductAction}
        banner={summariesInfo.CARDS.banner}
      >
        <CardContent list={listProducts.accounts} showSald={showSald} />
      </ContentContainer>
    ),
    onClick: () => {
      /* istanbul ignore next */
      switchSummary(summariesInfo.CARDS.id);
    },
  },
  {
    title: (
      <ProductsHeader
        reference={categorySectionRefs.CREDITS}
        onClick={() => simulateProductIconChevronClick(2)}
        product="Créditos"
      />
    ),
    content: () => (
      <div>
        {showBannerRTO && (
          <Margin md={24} xs={24} sm={24} lg={24} side="top">
            <RealTimeOffer locationParam="ACCORDION" />
          </Margin>
        )}
        <ContentContainer
          id={summariesInfo.CREDITS.id}
          loading={!summariesInfo.CREDITS.loaded && !error}
          error={error}
          action={() => {
            /* istanbul ignore next */
            recallService();
          }}
          newProductMessage={summariesInfo.CREDITS.newProductMessage}
          newProductAction={summariesInfo.CREDITS.newProductAction}
          banner={summariesInfo.CREDITS.banner}
        >
          <CreditContent list={listProducts.loans} showSald={showSald} />
        </ContentContainer>
      </div>
    ),
    onClick: () => {
      /* istanbul ignore next */
      switchSummary(summariesInfo.CREDITS.id);
    },
  },
  {
    title: (
      <ProductsHeader
        onClick={() => simulateProductIconChevronClick(3)}
        reference={categorySectionRefs.INVESTMENTS}
        product="Inversiones"
      />
    ),
    content: () => (
      <ContentContainer
        id={summariesInfo.INVESTMENTS.id}
        loading={!summariesInfo.INVESTMENTS.loaded && !error}
        error={error}
        action={() => {
          /* istanbul ignore next */
          recallService();
        }}
        newProductMessage={summariesInfo.INVESTMENTS.newProductMessage}
        newProductAction={summariesInfo.INVESTMENTS.newProductAction}
        banner={summariesInfo.INVESTMENTS.banner}
      >
        <InvestmentContent list={listProducts.investments} showSald={showSald} />
      </ContentContainer>
    ),
    onClick: () => {
      switchSummary(summariesInfo.INVESTMENTS.id);
    },
  },
];

cardAccordionItems.displayName = 'cardAccordionItems';

const ProductSummary = () => {
  const [showSald, setShowSaldo] = useState(false);
  const [listProducts, setListProducts] = useState([]);
  const [error, setError] = useState(false);
  const [currentSummary, setCurrentSummary] = useState(SUMMARY_PRODUCTS_INITIAL_STATE.ACCOUNTS.id);
  const [summariesInfo, setSummariesInfo] = useState(SUMMARY_PRODUCTS_INITIAL_STATE);
  const [bootstraped, isTheSummaryBootstrapped] = useState(false);
  const [customerIteratedForTheFirstTime, setCustomerIteratedForTheFirstTime] = useState(false);

  const categorySectionRefs = {
    ACCOUNTS: useRef(null),
    CARDS: useRef(null),
    CREDITS: useRef(null),
    INVESTMENTS: useRef(null),
  };

  const [showBannerRTO, setShowBannerRTO] = useState(false);
  const getSession = async () => {
    const session = await getAllInSession();
    setShowBannerRTO(session.T26000 !== undefined);
  };

  /* istanbul ignore next */
  const onHideSaldHandler = () => {
    setShowSaldo(!showSald);
    saveInStorage('showSald', !showSald);
  };
  /* istanbul ignore next */
  const isAllSummariesClosed = newSummarieInfo => {
    let allClosed = true;
    if (newSummarieInfo) {
      Object.keys(newSummarieInfo).forEach(key => {
        if (!newSummarieInfo[key].closed) allClosed = false;
      });
    } else return allClosed;
    return allClosed;
  };

  /* istanbul ignore next */
  const switchSummary = summaryId => {
    setCustomerIteratedForTheFirstTime(true);
    setError(false);
    let newSetSummariesInfo;
    if (summaryId === currentSummary) {
      newSetSummariesInfo = {
        ...summariesInfo,
        ...{
          [summariesInfo[summaryId].id]: {
            id: summariesInfo[summaryId].id,
            loaded: summariesInfo[summaryId].loaded,
            closed: !summariesInfo[summaryId].closed,
            eventName: summariesInfo[summaryId].eventName,
            newProductMessage: summariesInfo[summaryId].newProductMessage,
            newProductAction: summariesInfo[summaryId].newProductAction,
            banner: summariesInfo[summaryId].banner,
            mercury: summariesInfo[summaryId].mercury,
          },
        },
      };
    } else {
      newSetSummariesInfo = {
        ...summariesInfo,
        [summariesInfo[summaryId].id]: {
          id: summariesInfo[summaryId].id,
          loaded: summariesInfo[summaryId].loaded,
          closed: false,
          eventName: summariesInfo[summaryId].eventName,
          newProductMessage: summariesInfo[summaryId].newProductMessage,
          newProductAction: summariesInfo[summaryId].newProductAction,
          banner: summariesInfo[summaryId].banner,
          mercury: summariesInfo[summaryId].mercury,
        },
      };
      if (currentSummary) {
        newSetSummariesInfo = {
          ...newSetSummariesInfo,
          ...{
            [summariesInfo[currentSummary].id]: {
              id: summariesInfo[currentSummary].id,
              loaded: summariesInfo[currentSummary].loaded,
              closed: true,
              eventName: summariesInfo[currentSummary].eventName,
              newProductMessage: summariesInfo[currentSummary].newProductMessage,
              newProductAction: summariesInfo[currentSummary].newProductAction,
              banner: summariesInfo[currentSummary].banner,
              mercury: summariesInfo[currentSummary].mercury,
            },
          },
        };
      }
    }
    setSummariesInfo(newSetSummariesInfo);
    setCurrentSummary(isAllSummariesClosed(newSetSummariesInfo) ? null : summaryId);
    dlParentPush(SUMMARY_PRODUCTS_INITIAL_STATE[summaryId].eventName);
  };

  const autoScroll = () => {
    if (customerIteratedForTheFirstTime)
      categorySectionRefs[currentSummary].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
  };

  const getCustomerColorFromSessionStorage = async () => {
    const session = await getAllInSession();
    const customerType = JSON.parse(sessionStorage.getItem(session.SESSION.rut));
    return customerType !== undefined && customerType !== '' && customerType !== null
      ? customerType.customerColor
      : 'M';
  };
  /* istanbul ignore next */
  const callService = async () => {
    setError(false);
    const customerColor = await getCustomerColorFromSessionStorage();
    if (currentSummary) {
      switch (currentSummary) {
        case summariesInfo.CARDS.id: {
          if (!summariesInfo.CARDS.loaded) {
            getProducts({
              products: 'credit_cards',
              category: 'accounts',
            })
              .then(({ data }) => {
                if (data.accounts !== undefined) {
                  if (listProducts.accounts === undefined) {
                    listProducts.accounts = {};
                  }
                  listProducts.accounts.credit_cards = data.accounts.credit_cards;
                }

                setListProducts(listProducts);
                setSummariesInfo({
                  ...summariesInfo,
                  ...{
                    CARDS: {
                      id: 'CARDS',
                      loaded: true,
                      newProductMessage: summariesInfo.CARDS.newProductMessage,
                      newProductAction: summariesInfo.CARDS.newProductAction,
                      banner: summariesInfo.CARDS.banner,
                    },
                  },
                });
              })
              .then(() => {
                autoScroll();
              })
              .catch(() => {
                setError(true);
              });
          } else {
            autoScroll();
          }
          break;
        }
        case summariesInfo.CREDITS.id: {
          if (!summariesInfo.CREDITS.loaded) {
            getProducts({
              products: 'all',
              category: 'loans',
            })
              .then(({ data }) => {
                const loansData = data;
                listProducts.loans = loansData.loans;
                setListProducts(listProducts);
                setSummariesInfo({
                  ...summariesInfo,
                  ...{
                    CREDITS: {
                      id: 'CREDITS',
                      loaded: true,
                      newProductMessage: summariesInfo.CREDITS.newProductMessage,
                      newProductAction: summariesInfo.CREDITS.newProductAction,
                      banner: summariesInfo.CREDITS.banner,
                    },
                  },
                });
              })
              .then(() => {
                autoScroll();
              })
              .catch(() => {
                setError(true);
              });
          } else {
            autoScroll();
          }
          break;
        }
        case summariesInfo.INVESTMENTS.id: {
          if (!summariesInfo.INVESTMENTS.loaded) {
            getProducts({
              products: 'mutual_funds,term_deposits',
              category: 'investments',
              customerColor,
            })
              .then(({ data }) => {
                setListProducts({
                  ...listProducts,
                  ...{ investments: data.investments },
                });
                setSummariesInfo({
                  ...summariesInfo,
                  ...{
                    INVESTMENTS: {
                      id: 'INVESTMENTS',
                      loaded: true,
                      newProductMessage: summariesInfo.INVESTMENTS.newProductMessage,
                      newProductAction: summariesInfo.INVESTMENTS.newProductAction,
                      banner: summariesInfo.INVESTMENTS.banner,
                    },
                  },
                });
              })
              .then(() => {
                autoScroll();
              })
              .catch(() => {
                setError(true);
              });
          } else {
            autoScroll();
          }
          break;
        }
        default: {
          console.log('entrando en el default switch');
          if (!summariesInfo.ACCOUNTS.loaded) {
            getProducts({
              products: 'checking_accounts,debit_accounts,saving_accounts',
              category: 'accounts',
            })
              .then(({ data }) => {
                setListProducts(data);
                setSummariesInfo({
                  ...summariesInfo,
                  ...{
                    ACCOUNTS: {
                      id: 'ACCOUNTS',
                      loaded: true,
                      newProductMessage: summariesInfo.ACCOUNTS.newProductMessage,
                      newProductAction: summariesInfo.ACCOUNTS.newProductAction,
                      banner: summariesInfo.ACCOUNTS.banner,
                      mercury: summariesInfo.ACCOUNTS.mercury,
                    },
                  },
                });
              })
              .then(() => {
                autoScroll();
              })
              .catch(() => {
                setError(true);
              });
          } else {
            autoScroll();
          }
          if (!bootstraped) {
            simulateProductIconChevronClick(SUMMARY_PRODUCTS_INITIAL_STATE.ACCOUNTS.summaryPosition);
            isTheSummaryBootstrapped(true);
          }
          break;
        }
      }
    }
  };

  useEffect(() => {
    getInStorage('showSald').then(showSaldBool => {
      if (showSaldBool) setShowSaldo(showSaldBool);
    });
    callService();
    getSession();
  }, [currentSummary]);

  return (
    <div className={`${'canvas'}`}>
      <div style={{ paddingTop: '2.4rem' }} />
      <Direction>
        <Column xs={12} md={12} style={{ padding: '0px' }} className={styles['product-summary-accordion']}>
          <CardAccordion
            className={styles['product-summary-card-accordion']}
            items={cardAccordionItems({
              listProducts,
              showSald,
              onHideSaldHandler,
              error,
              switchSummary,
              summariesInfo,
              currentSummary,
              recallService: callService,
              categorySectionRefs,
              showBannerRTO,
            })}
          />
        </Column>
      </Direction>
      <Kyc />
    </div>
  );
};

ProductSummary.displayName = 'ProductSummary';

export default ProductSummary;
